import React, { useState } from 'react';
import './TopAlarmsSection.css';

const CONFIDENCE_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

const SOURCE_LABEL = {
  'metric-filter': 'metric filter on this log group',
  'service-convention': 'service log group',
  'name-match': 'log group name matches the alarm resource',
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

const RcaSummaryCell = ({ rca }) => {
  if (!rca) {
    return <span className="rca-muted">Not analysed yet</span>;
  }
  if (rca.status === 'failed') {
    return <span className="rca-muted">Analysis failed</span>;
  }
  if (rca.status === 'skipped' || !rca.analysis) {
    return <span className="rca-muted">Pending next run</span>;
  }
  return <span>{rca.analysis.summary}</span>;
};

const ConfidenceBadge = ({ rca }) => {
  const level = rca && rca.analysis && rca.analysis.confidence;
  if (!level) return <span className="rca-muted">—</span>;
  return (
    <span className={`rca-confidence rca-confidence-${level}`}>
      {CONFIDENCE_LABEL[level] || level}
    </span>
  );
};

const fmtValue = (v) => {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ') || '—';
  }
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
};

const fmtNum = (v) => (typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');

const ResourceConfig = ({ resources }) => {
  if (!resources || resources.length === 0) return null;
  return (
    <>
      <h4>Resource configuration</h4>
      {resources.map((r) => (
        <div key={`${r.type}-${r.id}`} className="rca-source">
          <div className="rca-source-head">
            <strong>{r.type}</strong> <code>{r.id}</code>
          </div>
          {r.error ? (
            <p className="rca-muted">Could not read the configuration: {r.error}</p>
          ) : (
            <dl className="rca-config">
              {Object.entries(r.config || {}).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt>{k}</dt>
                  <dd>{fmtValue(v)}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
      ))}
    </>
  );
};

const MetricSummary = ({ metrics }) => {
  const series = (metrics && metrics.series) || [];
  if (series.length === 0) {
    return metrics && metrics.error
      ? <p className="rca-muted">Metric data could not be read: {metrics.error}</p>
      : null;
  }
  return (
    <>
      <h4>Metrics (last 7 days, hourly)</h4>
      <table className="rca-signatures rca-metrics">
        <thead>
          <tr>
            <th scope="col">Metric</th>
            <th scope="col">Statistic</th>
            <th scope="col">Min</th>
            <th scope="col">Avg</th>
            <th scope="col">p95</th>
            <th scope="col">Max</th>
          </tr>
        </thead>
        <tbody>
          {series.map((s, i) => (
            <tr key={i}>
              <td>
                {s.metric}
                {s.role === 'alarm metric' && <span className="rca-muted"> (alarm)</span>}
              </td>
              <td>{s.stat}</td>
              <td>{s.datapoints ? fmtNum(s.min) : 'no data'}</td>
              <td>{fmtNum(s.avg)}</td>
              <td>{fmtNum(s.p95)}</td>
              <td>{fmtNum(s.max)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const RcaDetail = ({ alarm }) => {
  const rca = alarm.rca;

  if (!rca) {
    return (
      <p className="rca-muted">
        No analysis is available for this alarm yet. It will be analysed in the
        next scheduled run.
      </p>
    );
  }
  if (rca.status !== 'analyzed' || !rca.analysis) {
    return (
      <p className="rca-muted">
        {rca.error || 'No analysis is available for this alarm yet.'}
      </p>
    );
  }

  const a = rca.analysis;
  const sources = rca.logSources || [];

  return (
    <div className="rca-detail">
      <div className="rca-meta">
        <span>Category: <strong>{a.category}</strong></span>
        <span>Confidence: <ConfidenceBadge rca={rca} /></span>
        <span>Analysed: {fmtTime(rca.analyzedAt)}</span>
      </div>

      {!rca.logsLinked && (
        <p className="rca-warning" role="note">
          No log group could be read for this alarm, so this analysis is based on
          its metrics, the resource configuration and the alarm history.
        </p>
      )}

      <h4>Root cause</h4>
      <p>{a.rootCause}</p>

      {a.evidence && a.evidence.length > 0 && (
        <>
          <h4>Evidence</h4>
          <ul className="rca-evidence">
            {a.evidence.map((e, i) => <li key={i}><code>{e}</code></li>)}
          </ul>
        </>
      )}

      <h4>Recommended fix</h4>
      <p>{a.fix}</p>
      {a.fixSteps && a.fixSteps.length > 0 && (
        <ol className="rca-steps">
          {a.fixSteps.map((s, i) => <li key={i}><pre>{s}</pre></li>)}
        </ol>
      )}

      {a.prevention && (
        <>
          <h4>Prevention</h4>
          <p>{a.prevention}</p>
        </>
      )}

      {a.alarmTuning && (
        <>
          <h4>Alarm tuning</h4>
          <p>{a.alarmTuning}</p>
        </>
      )}

      <ResourceConfig resources={rca.resourceConfig} />
      <MetricSummary metrics={rca.metrics} />

      {sources.length > 0 && (
        <>
          <h4>Failures found in logs (last 7 days)</h4>
          {sources.map((s) => (
            <div key={s.logGroup} className="rca-source">
              <div className="rca-source-head">
                <code>{s.logGroup}</code>
                <span className="rca-muted"> · {SOURCE_LABEL[s.source] || s.source}</span>
                {typeof s.failureCount === 'number' && (
                  <span> · {s.failureCount}{s.failureCountIsLowerBound ? '+' : ''} failure line(s)</span>
                )}
              </div>
              {s.error ? (
                <p className="rca-muted">Could not read this log group: {s.error}</p>
              ) : (s.signatures || []).length === 0 ? (
                <p className="rca-muted">No failure lines in the window.</p>
              ) : (
                <table className="rca-signatures">
                  <thead>
                    <tr><th scope="col">Count</th><th scope="col">Failure</th></tr>
                  </thead>
                  <tbody>
                    {s.signatures.map((sig, i) => (
                      <tr key={i}>
                        <td>{sig.count}</td>
                        <td><code>{(sig.samples && sig.samples[0]) || sig.signature}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </>
      )}

      {rca.recentTriggers && rca.recentTriggers.length > 0 && (
        <>
          <h4>Recent triggers</h4>
          <ul className="rca-triggers">
            {rca.recentTriggers.map((t, i) => (
              <li key={i}><strong>{fmtTime(t.timestamp)}</strong> — {t.reason}</li>
            ))}
          </ul>
        </>
      )}

      <p className="rca-disclaimer">
        Generated by AI from redacted CloudWatch logs and alarm history. Check the
        evidence before applying a fix.
      </p>
    </div>
  );
};

const TopAlarmsSection = ({ topAlarms }) => {
  const [expanded, setExpanded] = useState(null);

  if (topAlarms === null) {
    return <div className="top-alarms-empty">Loading alarm activity…</div>;
  }
  const alarms = (topAlarms && topAlarms.alarms) || [];
  if (alarms.length === 0) {
    return (
      <div className="top-alarms-empty">
        No alarms triggered in the 7 days before the last scheduled run.
      </div>
    );
  }

  return (
    <>
      <div className="top-alarms-table-container">
        <table className="top-alarms-table top-alarms-rca-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Alarm</th>
              <th scope="col">Region</th>
              <th scope="col">Root cause (AI)</th>
              <th scope="col">Confidence</th>
              <th scope="col">Times Triggered</th>
            </tr>
          </thead>
          <tbody>
            {alarms.map((alarm, index) => {
              const rowKey = `${alarm.region}-${alarm.alarmName}`;
              const isOpen = expanded === rowKey;
              const detailId = `rca-detail-${index}`;
              return (
                <React.Fragment key={`${rowKey}-${index}`}>
                  <tr className={isOpen ? 'rca-row-open' : ''}>
                    <td>{index + 1}</td>
                    <td>
                      <button
                        type="button"
                        className="rca-toggle"
                        aria-expanded={isOpen}
                        aria-controls={detailId}
                        onClick={() => setExpanded(isOpen ? null : rowKey)}
                        title={alarm.stateReason || ''}
                      >
                        <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span> {alarm.alarmName}
                      </button>
                    </td>
                    <td>{alarm.region}</td>
                    <td className="rca-summary-cell"><RcaSummaryCell rca={alarm.rca} /></td>
                    <td><ConfidenceBadge rca={alarm.rca} /></td>
                    <td><strong>{alarm.triggerCount}</strong></td>
                  </tr>
                  {isOpen && (
                    <tr className="rca-detail-row">
                      <td colSpan={6} id={detailId}>
                        <RcaDetail alarm={alarm} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="top-alarms-footer">
        {/* The cadence differs per deployment (weekly in prod, monthly in
            dev), so show when each part was produced rather than a schedule. */}
        {topAlarms.lastUpdated && (
          <>Alarm counts updated {fmtTime(topAlarms.lastUpdated)}</>
        )}
        {topAlarms.rcaLastUpdated && (
          <> · Root-cause analysis {fmtTime(topAlarms.rcaLastUpdated)}</>
        )}
      </div>
    </>
  );
};

export default TopAlarmsSection;
